import styled from "@emotion/styled";
import { PropsWithChildren, useState } from "react";
import mobileBackground from "../../img/backgrounds/activity-bg-mobile.svg";
import tradeBackground from "../../img/backgrounds/trade-bg.svg";
import activityBackground from "../../img/backgrounds/activity-bg.svg";
import tradeBackgroundWide from "../../img/backgrounds/trade-bg-wide.svg";
import activityBackgroundWide from "../../img/backgrounds/activity-bg-wide.svg";
import { GlobalStore } from "store/global/GlobalStore";
import { MobileProfileBar } from "components/NavbarV2/MobileProfileBar";
import { NavbarV2Mobile } from "components/NavbarV2/NavbarV2Mobile";
import { MobileBottomSheet } from "components/general/MobileBottomSheet/MobileBottomSheet";
import { ProfileActionsContent } from "components/NavbarV2/elements/ProfileActions/elements/ProfileActionsContent";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "hooks/useDeviceType";
import { NavbarV2 } from "components/NavbarV2/NavbarV2";
import { SideNav } from "components/NavbarV2/elements/SideNav";
import { useToggleIntercom } from "hooks/useToggleIntercom";
import { Socials } from "components/NavbarV2/elements/Socials";
import { BEBOP_ROUTES } from "config/consts";

// Sandwiches all content (mobile, tablet and desktop) with a navigation bar
export const NavbarWrapper = ({ children }: PropsWithChildren) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const isConnected = GlobalStore.useState(s => s.address) !== "";
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const isActivityPage = (location.pathname as BEBOP_ROUTES) === BEBOP_ROUTES.Activity;

  useToggleIntercom();

  // Do not show mobile wrapper on home page
  if (location.pathname === "/") {
    return <>{children}</>;
  }

  if (!isMobile) {
    return (
      <>
        <ContainerDesktopOrTablet isActivityPage={isActivityPage}>
          <NavbarV2 />
          {children}
        </ContainerDesktopOrTablet>
        <NonMobileSocialsContainer>
          <Socials />
        </NonMobileSocialsContainer>
      </>
    );
  }

  const profileBarBottomSheet = (
    <MobileBottomSheet
      title={isConnected ? "Wallet" : "Start trading now"}
      isOpen={isBottomSheetOpen}
      setIsOpen={setIsBottomSheetOpen}
    >
      <ProfileActionsContent closeDisplay={() => setIsBottomSheetOpen(false)} />
    </MobileBottomSheet>
  );

  return (
    <MobileContainer>
      <MobileContent>
        <MobileProfileBar setIsBottomSheetOpen={setIsBottomSheetOpen} />
        <MobileScrollableContainer>
          <MobileScrollableContent>{children}</MobileScrollableContent>
        </MobileScrollableContainer>
        {profileBarBottomSheet}
        <SideNav isOpen={isMoreOpen} setIsOpen={setIsMoreOpen} isMobileNavBarDisplayed={true} />
      </MobileContent>
      <NavbarV2Mobile isMoreOpen={isMoreOpen} setIsMoreOpen={setIsMoreOpen} />
    </MobileContainer>
  );
};

// Sub-components

const ContainerDesktopOrTablet = styled.div<{ isActivityPage: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5%;
  background-image: url(${props => (props.isActivityPage ? activityBackground : tradeBackground)});
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: ${props => props.theme.theme["200"]};
  color: ${props => props.theme.font.primary.default};

  @media screen and (min-width: 1281px) {
    background-image: url(${props =>
      props.isActivityPage ? activityBackgroundWide : tradeBackgroundWide});
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100dvw;
  height: 100dvh;
  overflow: hidden;
`;

const MobileContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.theme["200"]};
  padding: 8px;
  height: calc(100dvh - 68px);
  overflow: hidden;
  background-image: url(${mobileBackground});
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: ${props => props.theme.theme["200"]};
  position: relative;
`;

// MobileScrollableContainer + MobileScrollableContent:
// Hides scrollbar but still still allows for scrolling
const MobileScrollableContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const MobileScrollableContent = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: -17px;
  overflow-y: scroll;
`;

const NonMobileSocialsContainer = styled.div`
  position: fixed;
  right: 76px;
  bottom: 20px;
`;
